import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('esi_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { API, BACKEND_URL };

export const authApi = {
  register: (body) => api.post('/auth/register', body).then((r) => r.data),
  login: (body) => api.post('/auth/login', body).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  changePassword: (body) => api.post('/auth/change-password', body).then((r) => r.data),
  checkEmail: (email) => api.post('/auth/check-email', { email }).then((r) => r.data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }).then((r) => r.data),
  resetPassword: (token, new_password) =>
    api.post('/auth/reset-password', { token, new_password }).then((r) => r.data),
};

export const quotesApi = {
  list: () => api.get('/quotes').then((r) => r.data),
  create: (body) => api.post('/quotes', body).then((r) => r.data),
  remove: (id) => api.delete(`/quotes/${id}`).then((r) => r.data),
};

export const policiesApi = {
  list: () => api.get('/policies').then((r) => r.data),
  create: (body) => api.post('/policies', body).then((r) => r.data),
  update: (id, body) => api.put(`/policies/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/policies/${id}`).then((r) => r.data),
};

export const documentsApi = {
  list: () => api.get('/documents').then((r) => r.data),
  upload: (file, category, name) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('name', name || file.name);
    return api.post('/documents', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  downloadUrl: (id) => `${API}/documents/${id}/download`,
  remove: (id) => api.delete(`/documents/${id}`).then((r) => r.data),
};

export const renewalsApi = {
  list: () => api.get('/renewals').then((r) => r.data),
};

export const messagesApi = {
  list: () => api.get('/messages').then((r) => r.data),
  create: (body) => api.post('/messages', body).then((r) => r.data),
};

export const adminApi = {
  stats: () => api.get('/admin/stats').then((r) => r.data),
  users: () => api.get('/admin/users').then((r) => r.data),
  quotes: () => api.get('/admin/quotes').then((r) => r.data),
  policies: () => api.get('/admin/policies').then((r) => r.data),
  messages: () => api.get('/admin/messages').then((r) => r.data),
  updateMessage: (id, status) => api.put(`/admin/messages/${id}`, { status }).then((r) => r.data),
};

export const contentApi = {
  getPublic: (lang) => api.get('/content', { params: lang && lang !== 'en' ? { lang } : {} }).then((r) => r.data),
  getAdmin: (lang) => api.get('/admin/content', { params: lang && lang !== 'en' ? { lang } : {} }).then((r) => r.data),
  update: (content, lang) => api.put('/admin/content', { content, lang: lang || 'en' }).then((r) => r.data),
  translate: (langs, only_missing = true) =>
    api.post('/admin/content/translate', { langs, only_missing }).then((r) => r.data),
};

export const i18nApi = {
  langs: () => api.get('/i18n/langs').then((r) => r.data),
  getStrings: (lang) => api.get(`/i18n/${lang}`).then((r) => r.data),
  translateOneLang: (lang, source, only_missing = true) =>
    api.post(`/admin/i18n/${lang}/auto`, { source, only_missing }).then((r) => r.data),
};

export const postsApi = {
  listPublic: (lang) => api.get('/posts', { params: lang && lang !== 'en' ? { lang } : {} }).then((r) => r.data),
  getPublic: (slug, lang) => api.get(`/posts/${slug}`, { params: lang && lang !== 'en' ? { lang } : {} }).then((r) => r.data),
  listAdmin: () => api.get('/admin/posts').then((r) => r.data),
  create: (body) => api.post('/admin/posts', body).then((r) => r.data),
  update: (id, body) => api.put(`/admin/posts/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/posts/${id}`).then((r) => r.data),
  translateAll: (langs, only_missing = true) =>
    api.post('/admin/posts/translate-all', { langs, only_missing }).then((r) => r.data),
  getTranslation: (id, lang) => api.get(`/admin/posts/${id}/translations/${lang}`).then((r) => r.data),
  setTranslation: (id, lang, body) => api.put(`/admin/posts/${id}/translations/${lang}`, body).then((r) => r.data),
};

export const uploadsApi = {
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  publicUrl: (relativeUrl) => {
    if (!relativeUrl) return '';
    if (/^https?:\/\//i.test(relativeUrl)) return relativeUrl;
    return `${BACKEND_URL}${relativeUrl}`;
  },
};

export const newsletterApi = {
  subscribe: (body) => api.post('/newsletter/subscribe', body).then((r) => r.data),
  list: () => api.get('/admin/subscribers').then((r) => r.data),
  remove: (id) => api.delete(`/admin/subscribers/${id}`).then((r) => r.data),
};

export const seoApi = {
  getPublic: () => api.get('/seo').then((r) => r.data),
  getAdmin: () => api.get('/admin/seo').then((r) => r.data),
  update: (seo) => api.put('/admin/seo', { seo }).then((r) => r.data),
};

export const storageApi = {
  report: () => api.get('/admin/storage/report').then((r) => r.data),
};

export const insurersApi = {
  listPublic: (lang, product) =>
    api.get('/insurers', { params: { ...(lang && lang !== 'en' ? { lang } : {}), ...(product ? { product } : {}) } }).then((r) => r.data),
  getPublic: (slug, lang) =>
    api.get(`/insurers/${slug}`, { params: lang && lang !== 'en' ? { lang } : {} }).then((r) => r.data),
  lookup: (names, lang) => api.post('/insurers/lookup', { names, lang }).then((r) => r.data),

  // Long-form articles (blog-style)
  listArticles: (lang) =>
    api.get('/insurer-articles', { params: lang && lang !== 'en' ? { lang } : {} }).then((r) => r.data),
  getArticle: (slug, lang) =>
    api.get(`/insurer-articles/${slug}`, { params: lang && lang !== 'en' ? { lang } : {} }).then((r) => r.data),

  listAdmin: () => api.get('/admin/insurers').then((r) => r.data),
  get: (id) => api.get(`/admin/insurers/${id}`).then((r) => r.data),
  create: (body) => api.post('/admin/insurers', body).then((r) => r.data),
  update: (id, body) => api.put(`/admin/insurers/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/insurers/${id}`).then((r) => r.data),
  seed: () => api.post('/admin/insurers/seed').then((r) => r.data),
  fetchLogos: (overwrite = false) =>
    api.post('/admin/insurers/fetch-logos', { overwrite }).then((r) => r.data),
  translateAll: (langs, only_missing = true) =>
    api.post('/admin/insurers/translate-all', { langs, only_missing }).then((r) => r.data),
};

export const taxLinkApi = {
  status: () => api.get('/tax-link/status').then((r) => r.data),
  profile: () => api.get('/tax-link/profile').then((r) => r.data),
  declarations: () => api.get('/tax-link/declarations').then((r) => r.data),
  prefill: () => api.get('/tax-link/prefill').then((r) => r.data),
};
